// Shared bits for the Salaires & Bonus screen (ported from the
// design/salaires-bonus prototype's sb-data.jsx helpers).

import { useState } from 'react';
import { ENTITY_BY_ID, type EntityId, type PayrollEmployeeDTO } from '@pointage/shared';
import { fmtDA } from '../../lib/format.js';

// The prototype's deterministic avatar palette (kept island-local —
// the rest of the app uses lib/saisie's palette, but the hashed
// per-person color is part of this design's look).
const SB_AVC = ['#7037ef', '#0bb5ab', '#ff5e11', '#d24df3', '#2a6fdb', '#c41d8f', '#0a9d72', '#d8590f'];

export function sbAvColor(id: string): string {
  return SB_AVC[[...id].reduce((a, c) => a + c.charCodeAt(0), 0) % SB_AVC.length]!;
}

export function sbInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]![0]! : (parts[0]?.[1] ?? '');
  return (first + last).toUpperCase();
}

export function entityShort(id: EntityId): string {
  return ENTITY_BY_ID[id]?.short ?? id;
}

export function entityColor(id: EntityId): string {
  return ENTITY_BY_ID[id]?.color ?? 'var(--shf)';
}

// Role line under a name: poste label when known, else the code.
export function roleLabel(e: PayrollEmployeeDTO): string {
  return e.posteLabel ?? e.code;
}

// Inline money display that flips to an input on click (prototype's
// SbMoney). Commits on blur/Enter, cancels on Escape.
export function MoneyButton({
  value,
  editable,
  onChange,
}: {
  value: number | null;
  editable: boolean;
  onChange?: (v: number) => void;
}) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState('');
  if (edit) {
    return (
      <input
        className="sb-medit"
        autoFocus
        value={v}
        onChange={(e) => setV(e.target.value.replace(/[^\d]/g, ''))}
        onBlur={() => {
          setEdit(false);
          if (v !== '' && onChange) onChange(Number(v));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') setEdit(false);
        }}
      />
    );
  }
  return (
    <button
      type="button"
      className={'sb-money' + (editable ? ' can' : '')}
      onClick={
        editable
          ? () => {
              setV(String(value ?? ''));
              setEdit(true);
            }
          : undefined
      }
    >
      {fmtDA(value)}
      {editable && <span className="sb-pen">✎</span>}
    </button>
  );
}

export const MONTH_LABELS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
] as const;

export function monthLabel(m: number): string {
  return MONTH_LABELS[m - 1] ?? String(m);
}
